import { observer } from "mobx-react-lite";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { Form as FinalForm, Field } from "react-final-form";
import {
  Form,
  Button,
  Grid,
  Segment,
  Dropdown,
  Table,
  Message,
} from "semantic-ui-react";
import {
  TeacherCategoryFormValues,
  TeacherFormValues,
} from "../../../../app/models/user";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import { combineValidators, isRequired } from "revalidate";
import SelectInput from "../../../../app/common/form/SelectInput";
import TextInput from "../../../../app/common/form/TextInput";
import { Link, RouteComponentProps } from "react-router-dom";
import DateInput from "../../../../app/common/form/DateInput";
import { CategoryForSelect } from "../../../../app/models/category";

const validate = combineValidators({
  email: isRequired({ message: "Email é obrigatório" }),
  gender: isRequired({ message: "Sexo é obrigatório" }),
});

interface DetailParams {
  id: string;
}

const TeacherForm: React.FC<RouteComponentProps<DetailParams>> = ({
  match,
}) => {
  const rootStore = useContext(RootStoreContext);
  const {
    createTeacher,
    loadTeacher,
    loadTeacherCategories,
    teacherCategoryByCatName,
    createTeacherCategory,
    deleteTeacherCategory,
    editTeacher,
    deleteTeacher,
    target,
  } = rootStore.teacherStore;
  const { loadCategoriesForSelect } = rootStore.categoryStore;

  const [teacher, setTeacher] = useState(new TeacherFormValues());
  const [categoriesForSelect, setCategoriesForSelect] = useState<
    CategoryForSelect[]
  >([]);
  const [categoryId, setCategoryId] = useState("");

  useEffect(() => {
    if (match.params.id) {
      loadTeacher(match.params.id).then((teacher) =>
        setTeacher(new TeacherFormValues(teacher))
      );

      loadTeacherCategories(match.params.id);
    }
    loadCategoriesForSelect().then((categories) => {
      let categoryList: CategoryForSelect[] = [];
      categories?.forEach((cat) => {
        let category = new CategoryForSelect(cat.id, cat.name, cat.id);
        categoryList.push(category);
      });

      categoryList.sort((a, b) => {
        return a.text.localeCompare(b.text);
      });

      setCategoriesForSelect(categoryList);
    });
  }, [
    loadTeacher,
    loadTeacherCategories,
    loadCategoriesForSelect,
    match.params.id,
  ]);

  const handleFinalFormSubmit = (values: any) => {
    const { ...teacher } = values;
    if (!teacher.id) {
      createTeacher(teacher);
    } else {
      editTeacher(teacher);
    }
  };

  const onChangeCategory = (categoryId: string | any) => {
    setCategoryId(categoryId);
  };

  const handleCreateTeacherCategory = (categoryId: string) => {
    var teacherCategory = new TeacherCategoryFormValues();
    teacherCategory.appUserId = match.params.id;
    teacherCategory.categoryId = categoryId;
    createTeacherCategory(teacherCategory);
  };

  const genderOptions = [
    { key: "", text: "", value: "" },
    { key: "M", text: "Masculino", value: "M" },
    { key: "F", text: "Feminino", value: "F" },
  ];

  return (
    <Fragment>
      {teacher.id ? <h1>Editar Professor</h1> : <h1>Novo Professor</h1>}
      <Segment clearing>
        <FinalForm
          onSubmit={handleFinalFormSubmit}
          validate={validate}
          initialValues={teacher!}
          render={({ handleSubmit, submitting, invalid, pristine }) => (
            <Form onSubmit={handleSubmit} error>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Field
                      name="firstName"
                      title="Nome"
                      placeholder="Nome"
                      component={TextInput}
                    />
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Field
                      name="lastName"
                      title="Sorenome"
                      placeholder="Sobrenome"
                      component={TextInput}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={4}>
                    <Field
                      name="gender"
                      title="Sexo"
                      placeholder="Sexo"
                      component={SelectInput}
                      options={genderOptions}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                      name="email"
                      title="Email"
                      placeholder="Email"
                      component={TextInput}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                      name="cpf"
                      title="CPF"
                      placeholder="CPF"
                      component={TextInput}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <Field
                      component={DateInput}
                      name="birthDate"
                      date={true}
                      title="Data Nascimento"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={1}>
                    <Field
                      name="phoneCode"
                      title="DDD"
                      placeholder="DDD"
                      component={TextInput}
                    />
                  </Grid.Column>
                  <Grid.Column width={3}>
                    <Field
                      name="phoneNumber"
                      title="Número de Telefone"
                      placeholder="Número de Telefone"
                      component={TextInput}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Button
                      loading={submitting}
                      disabled={invalid || pristine}
                      floated="right"
                      positive
                      type="submit"
                      content="Salvar"
                    />
                    <Button
                      as={Link}
                      to="/admin/professores"
                      floated="right"
                      type="button"
                      content="Cancelar"
                    />
                    {teacher.id && (
                      <Button
                        loading={target === teacher.id && submitting}
                        onClick={(e) => {
                          deleteTeacher(e, teacher.id!);
                          setTeacher(new TeacherFormValues());
                        }}
                        floated="right"
                        color="red"
                        content="Excluir"
                      />
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Form>
          )}
        />
      </Segment>
      {match.params.id && (
        <Fragment>
          <h2>Categorias</h2>
          <Segment>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Dropdown
                    placeholder="Categoria"
                    floating
                    selection
                    options={categoriesForSelect}
                    fluid
                    onChange={(e, data) => onChangeCategory(data.value)}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <Button
                    floated="left"
                    content="Associar"
                    primary
                    onClick={() => handleCreateTeacherCategory(categoryId)}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={16}>
                  {teacherCategoryByCatName.length > 0 ? (
                    <Table columns={16} striped>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell width={14}>
                            Categoria
                          </Table.HeaderCell>
                          <Table.HeaderCell textAlign="center"></Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {teacherCategoryByCatName.map((teacherCategory) => (
                          <Table.Row key={teacherCategory!.id}>
                            <Table.Cell>
                              {teacherCategory!.categoryName}
                            </Table.Cell>
                            <Table.Cell>
                              <Button
                                content="Excluir"
                                color="red"
                                onClick={(e) =>
                                  deleteTeacherCategory(e, teacherCategory!.id)
                                }
                              />
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  ) : (
                    <Message
                      icon="warning circle"
                      header="Oops"
                      content="Ainda não há dados suficientes"
                    />
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </Fragment>
      )}
    </Fragment>
  );
};

export default observer(TeacherForm);
